import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import MarketingShell from "@/components/MarketingShell";
import { formatShowDate, showDateParts } from "@/lib/showDate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goldCta =
  "inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] transition-colors hover:bg-[#F2D589]/[0.12]";
const goldStyle = { color: "#F2D589", border: "1px solid rgba(242,213,137,0.5)" };

export function ShowRow({ show, index = 0, testIdPrefix = "show", isPast = false }) {
  const { month, day, year } = showDateParts(show.date);
  return (
    <motion.li
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.06, 0.4) }}
      className="rounded-2xl border p-5 sm:p-6 backdrop-blur-md flex flex-col sm:flex-row sm:items-center gap-5"
      style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.45)" }}
      data-testid={`${testIdPrefix}-item`}
    >
      <div
        className="shrink-0 rounded-xl px-4 py-3 text-center"
        style={{ border: "1px solid rgba(242,213,137,0.3)", minWidth: "5.5rem" }}
      >
        <p className="text-[10px] uppercase tracking-[0.24em]" style={{ color: "#F2D589" }}>
          {month}
        </p>
        <p className="text-3xl leading-none mt-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          {day}
        </p>
        <p className="text-[10px] mt-1" style={{ color: "rgba(245,239,227,0.5)" }}>
          {year}
        </p>
      </div>

      <div className="flex-1 min-w-0">
        {show.kind ? (
          <p className="text-[10px] uppercase tracking-[0.28em] mb-1" style={{ color: "#FCA07A" }}>
            {show.kind}
          </p>
        ) : null}
        <h3
          className="text-2xl leading-tight"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          data-testid={`${testIdPrefix}-title`}
        >
          {show.title}
        </h3>
        <p className="mt-1.5 text-sm" style={{ color: "rgba(245,239,227,0.72)" }}>
          {[show.venue, show.city].filter(Boolean).join(" · ")}
          {show.time_label ? ` · ${show.time_label}` : ""}
        </p>
        {show.description ? (
          <p
            className="mt-3 text-sm leading-relaxed"
            style={{
              color: "rgba(245,239,227,0.62)",
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {show.description}
          </p>
        ) : null}
      </div>

      <div className="shrink-0 flex flex-col items-stretch gap-2">
        {show.ticket_url && !isPast ? (
          <a
            href={show.ticket_url}
            target="_blank"
            rel="noreferrer"
            className={goldCta}
            style={goldStyle}
            data-testid={`${testIdPrefix}-tickets`}
          >
            Tickets &rsaquo;
          </a>
        ) : (
          <span className="text-xs uppercase tracking-[0.2em] text-center" style={{ color: "rgba(245,239,227,0.45)" }}>
            {formatShowDate(show.date, { weekday: false })}
          </span>
        )}
        {show.rsvp_url && !isPast ? (
          <a
            href={show.rsvp_url}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] uppercase tracking-[0.2em] text-center transition-colors hover:text-[#F2D589]"
            style={{ color: "#FCA07A" }}
            data-testid={`${testIdPrefix}-rsvp`}
          >
            I&apos;m going
          </a>
        ) : null}
      </div>
    </motion.li>
  );
}

export default function Performances() {
  const [shows, setShows] = useState(null);
  const [artistUrl, setArtistUrl] = useState("");
  const [pastCount, setPastCount] = useState(0);
  const [past, setPast] = useState(null);
  const [pastOpen, setPastOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/shows`)
      .then(({ data }) => {
        if (!alive) return;
        const upcoming = data.shows || [];
        setShows(upcoming);
        setArtistUrl(data.artist_url || "");
        setPastCount(data.past_count || 0);
        // Nothing on the calendar? Show the archive instead of an empty page.
        if (upcoming.length === 0 && (data.past_count || 0) > 0) {
          setPastOpen(true);
          return axios.get(`${API}/shows`, { params: { past: 1 } }).then((r) => {
            if (alive) setPast(r.data.shows || []);
          });
        }
        return undefined;
      })
      .catch(() => {
        if (alive) setShows([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  const openPast = async () => {
    setPastOpen(true);
    if (past !== null) return;
    try {
      const { data } = await axios.get(`${API}/shows`, { params: { past: 1 } });
      setPast(data.shows || []);
    } catch {
      setPast([]);
    }
  };

  const followLink = artistUrl ? (
    <a
      href={`${artistUrl}${artistUrl.includes("?") ? "&" : "?"}trigger=track`}
      target="_blank"
      rel="noreferrer"
      className={goldCta}
      style={goldStyle}
      data-testid="performances-empty-follow"
    >
      Follow on Bandsintown
    </a>
  ) : null;

  const pastSection = (
    <section className="mt-14" data-testid="performances-past-section">
      <p className="text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: "#FCA07A" }}>
        Where the piano has been
      </p>
      {past === null ? (
        <p className="text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="performances-past-loading">
          Pulling the archive…
        </p>
      ) : past.length === 0 ? (
        <p className="text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="performances-past-empty">
          Nothing in the archive yet.
        </p>
      ) : (
        <ul className="grid gap-4 opacity-80" data-testid="performances-past-list">
          {past.map((s, i) => (
            <ShowRow key={s.id} show={s} index={i} testIdPrefix="past-performance" isPast />
          ))}
        </ul>
      )}
    </section>
  );

  return (
    <MarketingShell active="/performances">
      <motion.section
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        data-testid="performances-hero"
      >
        <p className="text-[10px] uppercase tracking-[0.32em] mb-3" style={{ color: "#F2D589" }}>
          Events &middot; Upcoming performances
        </p>
        <h1
          className="leading-[0.95]"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 500,
            fontSize: "clamp(40px, 5.4vw, 78px)",
            textWrap: "balance",
            maxWidth: "20ch",
          }}
        >
          Where the piano is <em style={{ color: "#F2D589" }}>next</em>.
        </h1>
        <p className="mt-5 max-w-[44rem] text-base sm:text-lg leading-relaxed" style={{ color: "rgba(245,239,227,0.8)" }}>
          Concerts, soundbaths and community nights on the calendar. Every show
          runs about 90 minutes, and Dan sticks around afterwards to farbreng
          with whoever wants to keep going.
        </p>
      </motion.section>

      <section className="mt-12" data-testid="performances-list-section">
        {shows === null ? (
          <p className="text-sm" style={{ color: "rgba(245,239,227,0.6)" }} data-testid="performances-loading">
            Checking the calendar…
          </p>
        ) : shows.length === 0 ? (
          <div
            className="rounded-2xl border px-6 py-12 sm:px-10 sm:py-16 text-center backdrop-blur-md"
            style={{ borderColor: "rgba(255,255,255,0.12)", backgroundColor: "rgba(8,4,30,0.45)" }}
            data-testid="performances-empty"
          >
            <p
              className="text-2xl sm:text-3xl"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontStyle: "italic" }}
            >
              No dates announced yet.
            </p>
            <p className="mt-4 text-sm max-w-[36rem] mx-auto" style={{ color: "rgba(245,239,227,0.7)" }}>
              {pastCount > 0
                ? "Here's where the piano has been in the meantime. Follow Dan on Bandsintown and you'll get a nudge the moment a show lands near you — most dates are booked directly by communities, so if you'd like one in your town, that starts with a conversation."
                : "Follow Dan on Bandsintown to be told first — you'll get a nudge the moment a show lands near you. Most dates are booked directly by communities, so if you'd like one in your town, that starts with a conversation."}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {followLink}
              <Link to="/community-events" className={goldCta} style={goldStyle} data-testid="performances-empty-community">
                Community events
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-white/25 transition-colors hover:bg-white/10"
                data-testid="performances-empty-contact"
              >
                Start a conversation
              </Link>
            </div>
          </div>
        ) : (
          <>
          {artistUrl ? (
            <div className="mb-6 flex justify-end">
              <a
                href={`${artistUrl}${artistUrl.includes("?") ? "&" : "?"}trigger=track`}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] uppercase tracking-[0.22em] transition-colors hover:text-[#F2D589]"
                style={{ color: "#FCA07A" }}
                data-testid="performances-follow"
              >
                Follow on Bandsintown &rarr;
              </a>
            </div>
          ) : null}
          <ul className="grid gap-4" data-testid="performances-list">
            {shows.map((s, i) => (
              <ShowRow key={s.id} show={s} index={i} testIdPrefix="performance" />
            ))}
          </ul>
          {pastCount > 0 && !pastOpen ? (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={openPast}
                className="text-[11px] uppercase tracking-[0.22em] transition-colors hover:text-[#F2D589]"
                style={{ color: "#FCA07A" }}
                data-testid="performances-show-past"
              >
                See past shows ({pastCount}) &darr;
              </button>
            </div>
          ) : null}
          </>
        )}
      </section>

      {pastOpen ? pastSection : null}

      <section
        className="mt-16 rounded-2xl border p-6 sm:p-8 backdrop-blur-md"
        style={{ borderColor: "rgba(242,213,137,0.28)", backgroundColor: "rgba(8,4,30,0.5)" }}
        data-testid="performances-book-block"
      >
        <h2 className="text-2xl sm:text-3xl" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
          Want a night like this in your community?
        </h2>
        <p className="mt-3 max-w-[46rem] text-sm sm:text-base" style={{ color: "rgba(245,239,227,0.72)", lineHeight: 1.7 }}>
          Concerts, soundbaths, galas and full Shabbatons — events start at
          $2,500.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/community-events" className={goldCta} style={goldStyle} data-testid="performances-cta-community">
            See the offerings
          </Link>
          <Link
            to="/private-events"
            className="inline-flex items-center justify-center rounded-full px-6 h-12 text-xs uppercase tracking-[0.22em] border border-white/25 transition-colors hover:bg-white/10"
            data-testid="performances-cta-private"
          >
            Private &amp; lifecycle events
          </Link>
        </div>
      </section>
    </MarketingShell>
  );
}
